/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react"

// porp-types is a library for typechecking of props
import PropTypes from "prop-types"

// react-chartjs-2 components
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"

// @mui material components
import Card from "@mui/material/Card"
import Divider from "@mui/material/Divider"
import Icon from "@mui/material/Icon"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"

// ReportsBarChart configurations
import configs from "monitor/charts/BarCharts/ReportsBarChart/configs"
import { Badge, Grid, IconButton } from "@mui/material"
import { FilterAlt } from "@mui/icons-material"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

function ReportsBarChart({
  color,
  title,
  description,
  dateIni,
  dateEnd,
  onPreviousClick,
  onNextClick,
  chart,
  filter,
  onSearch,
  filterClick,
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {})

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height="12.5rem"
            >
              <Bar data={data} options={options} redraw />
            </MDBox>
          ),
          [color, chart]
        )}
        <MDBox pt={3} pb={1} px={1}>
          <MDTypography variant="h6" textTransform="capitalize">
            {title}
          </MDTypography>
          <MDTypography component="div" variant="button" color="text" fontWeight="light">
            {description}
          </MDTypography>
          <Divider />
          <MDBox display="flex" alignItems="center">
            <MDTypography
              variant="button"
              color="success"
              lineHeight={1}
              sx={{ mt: 0.15, mr: 0.5, cursor: "pointer" }}
              onClick={onPreviousClick}
            >
              <Icon>navigate_before</Icon>
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="light">
              del {dateIni} al {dateEnd}
            </MDTypography>
            <MDTypography
              variant="button"
              color="success"
              lineHeight={1}
              onClick={onNextClick}
              sx={{ mt: 0.15, ml: 0.5, cursor: "pointer" }}
            >
              <Icon>navigate_next</Icon>
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  )
}

// Setting default values for the props of ReportsBarChart
ReportsBarChart.defaultProps = {
  color: "info",
  description: "",
  filter: false,
  onNextClick: () => {},
  onPreviousClick: () => {},
  onSearch: () => {},
  filterClick: () => {},
}

// Typechecking props for the ReportsBarChart
ReportsBarChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  dateIni: PropTypes.string.isRequired,
  dateEnd: PropTypes.string.isRequired,
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  filter: PropTypes.bool,
  onNextClick: PropTypes.func,
  onPreviousClick: PropTypes.func,
  onSearch: PropTypes.func,
  filterClick: PropTypes.func,
}

export default ReportsBarChart
